import React, { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];

export default function Timetable() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const canWrite = hasRole("super_admin", "direction", "administration", "coordinateur");
  const [dim, setDim] = useState("group"); // group|level|teacher|room
  const [refs, setRefs] = useState({ groups: [], levels: [], teachers: [], rooms: [], subjects: [] });
  const [target, setTarget] = useState("");
  const [slots, setSlots] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { day_of_week: "Lundi", start_time: "09:00", end_time: "10:30", group_id: "", subject_id: "", teacher_id: "", room_id: "", slot_type: "Cours" };
  const [form, setForm] = useState(empty);

  useEffect(() => {
    (async () => {
      try {
        const [g, l, te, r] = await Promise.all([
          api.get("/school/groups"), api.get("/school/levels", { params: { active_only: true, with_counts: false } }), api.get("/teachers"), api.get("/school/rooms"),
        ]);
        let subjects = [];
        try { subjects = (await api.get("/subjects")).data; } catch (e) {}
        setRefs({ groups: g.data, levels: l.data, teachers: te.data, rooms: r.data, subjects });
        if (g.data[0]) setTarget(g.data[0].id);
      } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
      if (canWrite) { try { setConflicts((await api.get("/school/timetable-conflicts")).data); } catch (e) {} }
    })();
  }, []);

  const loadSlots = async () => {
    const param = { group: "group_id", level: "official_level_id", teacher: "teacher_id", room: "room_id" }[dim];
    try { const { data } = await api.get("/school/timetable-slots", { params: target ? { [param]: target } : {} }); setSlots(data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  useEffect(() => { if (target || dim) loadSlots(); /* eslint-disable-next-line */ }, [dim, target]);

  const dimOptions = { group: refs.groups, level: refs.levels, teacher: refs.teachers, room: refs.rooms }[dim] || [];
  const optLabel = (o) => o.group_name || o.name || o.room_name || o.group_code;

  const byDay = useMemo(() => {
    const m = {}; DAYS.forEach((d) => (m[d] = []));
    slots.forEach((s) => { if (m[s.day_of_week]) m[s.day_of_week].push(s); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")));
    return m;
  }, [slots]);

  const submit = async () => {
    try {
      const { data } = await api.post("/school/timetable-slots", form);
      if (data.warnings && data.warnings.length) toast.warning(data.warnings.join(" "));
      else toast.success("Créneau créé.");
      setOpen(false); setForm(empty); loadSlots();
      if (canWrite) setConflicts((await api.get("/school/timetable-conflicts")).data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && d.conflicts) toast.error(d.conflicts.join(" "));
      else toast.error(apiErr(d));
    }
  };
  const remove = async (s) => {
    if (!window.confirm(`${t("delete")} ?`)) return;
    try { await api.delete(`/school/timetable-slots/${s.id}`); loadSlots(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="school-timetable">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-display font-bold text-slate-900">{t("timetable")}</h1>
        {canWrite && <Button data-testid="slot-add-btn" onClick={() => { setForm(empty); setOpen(true); }} className="rounded-full gap-2"><Plus className="h-4 w-4" />{t("newSlot")}</Button>}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Select value={dim} onValueChange={(v) => { setDim(v); setTarget(""); }}>
          <SelectTrigger data-testid="tt-dim" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="group">{t("byGroup")}</SelectItem>
            <SelectItem value="level">{t("byLevel")}</SelectItem>
            <SelectItem value="teacher">{t("byTeacher")}</SelectItem>
            <SelectItem value="room">{t("byRoom")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger data-testid="tt-target" className="w-56"><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>{dimOptions.map((o) => <SelectItem key={o.id} value={o.id}>{optLabel(o)}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {canWrite && (
        <div className={`mb-4 flex items-center gap-2 text-sm rounded-lg px-3 py-2 ${conflicts.length ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`} data-testid="tt-conflict-banner">
          <AlertTriangle className="h-4 w-4" />
          {conflicts.length ? `${conflicts.length} ${t("conflictsFound")}` : t("noConflict")}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {DAYS.map((day) => (
          <div key={day} data-testid={`tt-col-${day}`}>
            <div className="text-sm font-semibold text-slate-600 mb-2 px-1">{day}</div>
            <div className="space-y-2">
              {byDay[day].length === 0 ? <div className="text-xs text-slate-300 px-1">—</div> :
                byDay[day].map((s) => (
                  <Card key={s.id} data-testid="tt-slot" className="border-slate-200 p-3 relative overflow-hidden"
                    style={{ borderLeft: `4px solid ${s.color_tag || "#2081C3"}` }}>
                    <div className="text-xs font-medium text-slate-500 tabular-nums">{s.start_time}–{s.end_time}</div>
                    <div className="font-semibold text-slate-900 text-sm">{s.subject_name || s.slot_type}</div>
                    <div className="text-xs text-slate-600">{s.group_name}</div>
                    <div className="text-xs text-slate-500">{s.teacher_name} · {s.room_name || "—"}</div>
                    {canWrite && <button onClick={() => remove(s)} className="absolute top-2 right-2 text-slate-300 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>}
                  </Card>
                ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{t("newSlot")}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.day_of_week} onValueChange={(v) => setForm({ ...form, day_of_week: v })}>
                <SelectTrigger data-testid="slot-day"><SelectValue /></SelectTrigger>
                <SelectContent>{DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.slot_type} onValueChange={(v) => setForm({ ...form, slot_type: v })}>
                <SelectTrigger data-testid="slot-type"><SelectValue /></SelectTrigger>
                <SelectContent>{["Cours", "Soutien", "Étude", "Évaluation", "Atelier"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="slot-start" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
              <Input data-testid="slot-end" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
            </div>
            <Select value={form.group_id} onValueChange={(v) => setForm({ ...form, group_id: v })}>
              <SelectTrigger data-testid="slot-group"><SelectValue placeholder={t("group")} /></SelectTrigger>
              <SelectContent>{refs.groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.group_name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.subject_id} onValueChange={(v) => setForm({ ...form, subject_id: v })}>
              <SelectTrigger data-testid="slot-subject"><SelectValue placeholder={t("subject")} /></SelectTrigger>
              <SelectContent>{refs.subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
              <SelectTrigger data-testid="slot-teacher"><SelectValue placeholder={t("teacher")} /></SelectTrigger>
              <SelectContent>{refs.teachers.filter((x) => x.status !== "archived").map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.room_id} onValueChange={(v) => setForm({ ...form, room_id: v })}>
              <SelectTrigger data-testid="slot-room"><SelectValue placeholder={t("room")} /></SelectTrigger>
              <SelectContent>{refs.rooms.map((x) => <SelectItem key={x.id} value={x.id}>{x.room_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button><Button data-testid="slot-save-btn" onClick={submit}>{t("save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
